import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';

interface ReadingItem {
  id: string;
  type: string;
  typeLabel: string;
  typeEmoji: string;
  summary: string;
  fullText: string;
  createdAt: string;
}

type FilterType = 'all' | 'birth-chart' | 'forecast' | 'tarot' | 'questions' | 'synastry' | 'astromatch' | 'numerology' | 'i-ching' | 'dream-journal' | 'human-design' | 'vedic';

const FILTER_OPTIONS: { key: FilterType; label: string; emoji: string }[] = [
  { key: 'all',          label: 'Todas',           emoji: '📖' },
  { key: 'birth-chart',  label: 'Mapa Astral',      emoji: '🌠' },
  { key: 'forecast',     label: 'Previsões',        emoji: '🌙' },
  { key: 'tarot',        label: 'Tarot',            emoji: '🃏' },
  { key: 'questions',    label: 'Perguntas',        emoji: '⭐' },
  { key: 'synastry',     label: 'Sinastria',        emoji: '💫' },
  { key: 'astromatch',   label: 'AstroMatch',       emoji: '🔮' },
  { key: 'numerology',   label: 'Numerologia',      emoji: '🔢' },
  { key: 'i-ching',      label: 'I Ching',          emoji: '☯️' },
  { key: 'dream-journal',label: 'Sonhos',           emoji: '💭' },
  { key: 'human-design', label: 'Design Humano',    emoji: '⚡' },
  { key: 'vedic',        label: 'Védica',           emoji: '🕉️' },
];

async function fetchAllReadings(userId: string): Promise<ReadingItem[]> {
  const results: ReadingItem[] = [];

  // Questions
  const { data: questions } = await supabase
    .from('astrological_questions')
    .select('id, question, answer, created_at')
    .eq('user_id', userId)
    .eq('status', 'completed')
    .order('created_at', { ascending: false })
    .limit(50);
  if (questions) {
    results.push(...questions.map(q => ({
      id: q.id, type: 'questions', typeLabel: 'Pergunta aos Astros', typeEmoji: '⭐',
      summary: q.question?.slice(0, 80) ?? '',
      fullText: q.answer ?? '',
      createdAt: q.created_at,
    })));
  }

  // Synastry
  const { data: synastry } = await supabase
    .from('synastry_readings')
    .select('id, partner_name, reading_text, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (synastry) {
    results.push(...synastry.map(s => ({
      id: s.id, type: 'synastry', typeLabel: 'Sinastria', typeEmoji: '💫',
      summary: s.partner_name ? `Sinastria com ${s.partner_name}` : 'Sinastria',
      fullText: s.reading_text ?? '',
      createdAt: s.created_at,
    })));
  }

  // Dream journal
  const { data: dreams } = await supabase
    .from('dream_entries')
    .select('id, dream_text, interpretation, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(30);
  if (dreams) {
    results.push(...dreams.map(d => ({
      id: d.id, type: 'dream-journal', typeLabel: 'Diário dos Sonhos', typeEmoji: '💭',
      summary: d.dream_text?.slice(0, 80) ?? '',
      fullText: d.interpretation ?? '',
      createdAt: d.created_at,
    })));
  }

  // Reading analytics (covers birth-chart, forecast, tarot, numerology, i-ching, astromatch, human-design, vedic)
  const { data: analytics } = await supabase
    .from('reading_analytics')
    .select('id, feature, reading_text, created_at')
    .eq('user_id', userId)
    .not('reading_text', 'is', null)
    .order('created_at', { ascending: false })
    .limit(100);
  if (analytics) {
    const featureMap: Record<string, { label: string; emoji: string; type: FilterType }> = {
      'birth-chart':    { label: 'Mapa Astral',   emoji: '🌠', type: 'birth-chart' },
      'forecast':       { label: 'Previsão',       emoji: '🌙', type: 'forecast' },
      'tarot-reading':  { label: 'Tarot',          emoji: '🃏', type: 'tarot' },
      'numerology':     { label: 'Numerologia',    emoji: '🔢', type: 'numerology' },
      'i-ching':        { label: 'I Ching',        emoji: '☯️', type: 'i-ching' },
      'astromatch-analyze': { label: 'AstroMatch', emoji: '🔮', type: 'astromatch' },
      'human-design':   { label: 'Design Humano',  emoji: '⚡', type: 'human-design' },
      'vedic-astrology':{ label: 'Védica',         emoji: '🕉️', type: 'vedic' },
    };
    results.push(...analytics
      .filter(a => featureMap[a.feature])
      .map(a => ({
        id: a.id,
        type: featureMap[a.feature].type,
        typeLabel: featureMap[a.feature].label,
        typeEmoji: featureMap[a.feature].emoji,
        summary: a.reading_text?.slice(0, 80) ?? '',
        fullText: a.reading_text ?? '',
        createdAt: a.created_at,
      })));
  }

  // Sort by date descending
  results.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return results;
}

export function ReadingsPage() {
  const { profile }    = useAuth();
  const { t }          = useI18n();
  const [readings,     setReadings]    = useState<ReadingItem[]>([]);
  const [loading,      setLoading]     = useState(true);
  const [filter,       setFilter]      = useState<FilterType>('all');
  const [expanded,     setExpanded]    = useState<string | null>(null);

  useEffect(() => {
    if (profile?.id) {
      setLoading(true);
      fetchAllReadings(profile.id).then(data => {
        setReadings(data);
        setLoading(false);
      });
    }
  }, [profile?.id]);

  const filtered = filter === 'all' ? readings : readings.filter(r => r.type === filter);

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.readings.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.readings.subtitle')}
        </p>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', marginBottom: '1.5rem', paddingBottom: '0.25rem' }}>
        {FILTER_OPTIONS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {f.emoji} {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card shimmer" style={{ height: '72px' }} />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <EmptyState
          emoji="📖"
          title={filter === 'all' ? t('pages.readings.emptyAll') : t('pages.readings.emptyFilter')}
          subtitle={filter === 'all' ? 'Suas leituras aparecerão aqui depois da primeira consulta.' : 'Tente outro filtro ou faça uma nova consulta.'}
        />
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(item => (
            <div key={item.id} className="card" style={{ padding: '0' }}>
              <button
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}
                onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1rem' }}>{item.typeEmoji}</span>
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {item.typeLabel}
                    </span>
                  </div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.summary || t('pages.readings.reading')}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                    {new Date(item.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                {expanded === item.id
                  ? <ChevronUp size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  : <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
              </button>
              {expanded === item.id && (
                <div className="animate-fade-in" style={{ padding: '0 1rem 1rem', borderTop: '1px solid var(--border-subtle)' }}>
                  <div className="reading-text" style={{ marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                    {(item.fullText || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
